/**
 * ProposedActions — acting on an answer, in the product that owns the action.
 *
 * Ask Phibian answers a question; where the domain supports it, the answer
 * carries actions a person can take. This component renders them and nothing
 * else: it composes no request body beyond the three closed values the server
 * sent, it makes no authorization decision, and it treats a 403 as an ordinary
 * outcome rather than an impossible one.
 *
 * WHAT IT DOES NOT DO, and each of these is deliberate:
 *
 *   * It does not decide whether an action may be taken. `actionProposals`
 *     already contains only actions the server offered THIS caller, and the
 *     server re-authorizes on execute against a freshly-resolved context. A
 *     button that appears is not a grant, so a refusal here is not a bug.
 *   * It does not render a withheld action as a disabled version of the real
 *     one. A greyed-out "Notify Sunrise Program" would disclose the program;
 *     `withheldActions` carries closed tokens that name nothing, and that is
 *     all this renders.
 *   * It does not open a new destination. An executed action lands in
 *     Messages or in Operations - Tasks, screens that already exist, and the
 *     confirmation line links there. There is no seventh hub and no
 *     Intelligence-owned outbox.
 *   * It does not compose text. `label` was written by the server from a
 *     template table; nothing on this screen was authored by a model.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { intelligence, messaging } from '@cwos/api-client';

type OfferedAction = intelligence.OfferedAction;
type WithheldAction = intelligence.WithheldAction;
type ActionLanding = intelligence.ActionLanding;

/**
 * The one sentence each refusal template says. The server sends a template ID,
 * never prose, so the words a person reads live here where they can be
 * reviewed and translated.
 */
const REFUSAL_TEXT: Record<string, string> = {
  'action.permission_denied': 'You do not have permission to take this kind of action.',
  'action.scope_denied': 'You have this permission, but not where this action would apply.',
  'action.self_target_mismatch': 'This action can only be taken on your own records.',
  'action.prohibited_risk_class': 'This action is never taken automatically.',
  'denial.fail_closed': 'This action is not available.',
};

const FAIL_CLOSED_TEXT = 'This action is not available.';

function refusalText(withheld: WithheldAction): string {
  return REFUSAL_TEXT[withheld.explanation.template] ?? FAIL_CLOSED_TEXT;
}

function landingLink(landing: ActionLanding): { to: string; label: string } {
  if (landing.recordKind === 'message') {
    return landing.channelId !== undefined
      ? { to: `/messages/${landing.channelId}`, label: 'Open it in Messages' }
      : { to: '/messages', label: 'Open Messages' };
  }
  return { to: `/tasks/${landing.recordId}`, label: 'Open it in Tasks' };
}

/**
 * One offered action. A message action needs a channel first: the person picks
 * it, not Phibian, and the messaging domain re-checks that the channel belongs
 * to the program before it writes anything.
 */
function ActionRow({ action }: { action: OfferedAction }) {
  const [channelId, setChannelId] = useState('');
  const [landing, setLanding] = useState<ActionLanding | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  // The caller's OWN channels: a membership-scoped read the person could make
  // on the Messages screen anyway, filtered to the program the action names, so
  // the picker discloses nothing the product does not.
  const channels = useQuery({
    queryKey: ['messaging', 'channels'],
    queryFn: () => messaging.listChannels(),
    enabled: action.requiresChannel,
  });
  const eligible = (channels.data ?? []).filter(
    (channel) => channel.programId === action.params.subjectId && channel.status !== 'archived',
  );

  const execute = useMutation({
    mutationFn: () =>
      intelligence.executeAction({
        actionType: action.actionType,
        params: action.params,
        ...(action.requiresChannel && channelId !== '' ? { channelId } : {}),
      }),
    onSuccess: (response) => {
      setRefused(null);
      setLanding(response.landing);
    },
    onError: (error: unknown) => {
      // A REFUSAL IS AN ORDINARY OUTCOME. The offer was made against grants
      // that may since have changed, and the server is the authority on that -
      // not this button's existence.
      const status = (error as { status?: number } | null)?.status;
      setRefused(
        status === 403
          ? 'You are not authorized to take this action.'
          : status === 409
            ? 'That action is no longer available. Ask again to get a fresh one.'
            : 'That action could not be completed.',
      );
    },
  });

  if (landing !== null) {
    const link = landingLink(landing);
    return (
      <li className="ap-action ap-action-done">
        <p className="tc-note" role="status">
          Done. {landing.recordKind === 'message' ? 'The message was sent.' : 'The task was filed.'}{' '}
          <Link to={link.to}>{link.label}</Link>
        </p>
      </li>
    );
  }

  const needsChannel = action.requiresChannel;
  const blocked = needsChannel && (eligible.length === 0 || channelId === '');

  return (
    <li className="ap-action">
      <div className="ap-action-head">
        <span className="ap-action-label">{action.label}</span>
        <button
          type="button"
          className="rn-btn-quiet"
          disabled={execute.isPending || blocked}
          onClick={() => execute.mutate()}
        >
          {execute.isPending ? 'Working...' : 'Do it'}
        </button>
      </div>
      {needsChannel ? (
        channels.isLoading ? (
          <p className="tc-note">Loading channels...</p>
        ) : eligible.length === 0 ? (
          <p className="tc-note" role="note">
            You are not in a channel for this program, so there is nowhere to post it.
          </p>
        ) : (
          <label className="ap-action-channel">
            <span>Post in</span>
            <select value={channelId} onChange={(event) => setChannelId(event.target.value)}>
              <option value="">Choose a channel...</option>
              {eligible.map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.name ?? 'Channel'}
                </option>
              ))}
            </select>
          </label>
        )
      ) : null}
      {refused !== null ? (
        <p className="tc-note ap-caution" role="alert">
          {refused}
        </p>
      ) : null}
    </li>
  );
}

export function ProposedActions({
  actions,
  withheld,
}: {
  actions: readonly OfferedAction[] | undefined;
  withheld: readonly WithheldAction[] | undefined;
}) {
  const offered = actions ?? [];
  const refusals = withheld ?? [];
  if (offered.length === 0 && refusals.length === 0) return null;
  return (
    <div className="ap-actions">
      {offered.length > 0 ? (
        <>
          <p className="ap-actions-head">You can act on this</p>
          <ul className="ap-action-list">
            {offered.map((action) => (
              <ActionRow
                key={`${action.actionType}:${action.confirmation.proposalId}`}
                action={action}
              />
            ))}
          </ul>
        </>
      ) : null}
      {refusals.map((entry, index) => (
        // KEYED BY INDEX ON PURPOSE. A withheld entry has no id, because giving
        // it one would mean giving it a subject - which is exactly what it
        // exists not to carry.
        <p className="tc-note ap-caution" role="note" key={`withheld-${index}`}>
          {refusalText(entry)}
          {entry.alternatives.escalateToAdmin
            ? ' You can ask an administrator to do it.'
            : ' You can keep working here.'}
        </p>
      ))}
    </div>
  );
}
